import axios, { AxiosError } from 'axios';
import type { AxiosInstance } from 'axios';
import type {
  LoginRequest,
  LoginResponse,
  Conversation,
  Message,
  SendMessageRequest,
  AssignConversationRequest,
  Department,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
  Notification,
  ConversationQueryParams,
  NotificationQueryParams,
  ApiError,
  User,
  CreateUserRequest,
  UpdateUserRequest,
  InternalMessage,
  InternalChatConversation,
  ConversationRating,
  RatingStats,
  Contact,
  ContactsResponse,
  ContactQueryParams,
  CreateContactRequest,
  UpdateContactRequest,
  ImportContactsResult,
  ImportFromConversationsResult,
  Tag,
  CreateTagRequest,
  UpdateTagRequest,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle 401 errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        if (error.response?.status === 401) {
          const token = localStorage.getItem('token');
          const agent = localStorage.getItem('agent');

          // Log detailed information about the 401 error
          console.error('[AUTH ERROR 401] Unauthorized request detected:', {
            timestamp: new Date().toISOString(),
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            fullUrl: `${error.config?.baseURL || ''}${error.config?.url || ''}`,
            errorMessage: error.response?.data?.error || error.message,
            hasToken: !!token,
            tokenPrefix: token ? token.substring(0, 20) + '...' : 'none',
            hasAgent: !!agent,
            agentName: agent ? JSON.parse(agent)?.name : 'none',
            responseData: error.response?.data,
          });

          // Only redirect if we're not already on the login page to prevent loops
          if (window.location.pathname !== '/login') {
            localStorage.removeItem('token');
            localStorage.removeItem('agent');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/auth/login', data);
    return response.data;
  }

  async logout(): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>('/auth/logout');
    return response.data;
  }

  async updateAdminProfile(data: {
    name: string;
    email: string;
    currentPassword?: string;
    newPassword?: string;
  }): Promise<{ agent: User }> {
    const response = await this.client.put<{ agent: User }>('/auth/profile', data);
    return response.data;
  }

  // Conversations
  async getConversations(
    params?: ConversationQueryParams,
    skipCache = false
  ): Promise<{
    data: { conversations: Conversation[], pagination: { page: number, limit: number, total: number, totalPages: number } };
    fromCache: boolean;
  }> {
    const headers: Record<string, string> = {};
    if (skipCache) {
      headers['X-Skip-Cache'] = 'true';
    }

    const response = await this.client.get<{ conversations: Conversation[], pagination: { page: number, limit: number, total: number, totalPages: number } }>('/conversations', {
      params,
      headers,
    });

    return {
      data: response.data,
      fromCache: response.headers['x-from-cache'] === 'true',
    };
  }

  async getConversationIds(params?: ConversationQueryParams): Promise<{ ids: string[]; count: number }> {
    const response = await this.client.get<{ ids: string[]; count: number }>('/conversations/ids', {
      params,
    });
    return response.data;
  }

  async getConversation(id: string, skipCache = false): Promise<Conversation> {
    const response = await this.client.get<{ conversation: Conversation }>(`/conversations/${id}`, {
      headers: skipCache ? { 'x-skip-cache': 'true' } : {}
    });
    return response.data.conversation;
  }

  async assignConversation(id: string, data: AssignConversationRequest): Promise<Conversation> {
    const response = await this.client.put<{ conversation: Conversation }>(`/conversations/${id}/assign`, data);
    return response.data.conversation;
  }

  async transferConversation(id: string, data: { departmentId: string; agentId?: string }): Promise<Conversation> {
    const response = await this.client.put<{ conversation: Conversation }>(`/conversations/${id}/transfer`, data);
    return response.data.conversation;
  }

  async closeConversation(id: string): Promise<Conversation> {
    const response = await this.client.put<{ conversation: Conversation }>(`/conversations/${id}/close`);
    return response.data.conversation;
  }

  async closeAllConversations(): Promise<{ message: string; count: number }> {
    const response = await this.client.put<{ message: string; count: number }>('/conversations/bulk/close-all');
    return response.data;
  }

  async closeSelectedConversations(conversationIds: string[]): Promise<{ message: string; count: number }> {
    const response = await this.client.put<{ message: string; count: number }>('/conversations/bulk/close-selected', {
      conversationIds
    });
    return response.data;
  }

  async deleteConversation(id: string): Promise<{ message: string }> {
    const response = await this.client.delete<{ message: string }>(`/conversations/${id}`);
    return response.data;
  }

  async markConversationAsUnread(id: string): Promise<{ message: string; success: boolean }> {
    const response = await this.client.put<{ message: string; success: boolean }>(`/conversations/${id}/mark-as-unread`);
    return response.data;
  }

  async getConversationHistory(phone: string): Promise<any[]> {
    const response = await this.client.get<{ history: any[] }>(`/conversations/history/${phone}`);
    return response.data.history;
  }

  async getStats(params?: { agentId?: string; departmentId?: string }): Promise<{
    day: { total: number; active: number; queued: number; closed: number };
    week: { total: number; active: number; queued: number; closed: number };
    month: { total: number; active: number; queued: number; closed: number };
    total: { total: number; active: number; closed: number };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.agentId) queryParams.append('agentId', params.agentId);
    if (params?.departmentId) queryParams.append('departmentId', params.departmentId);

    const url = queryParams.toString()
      ? `/conversations/stats?${queryParams.toString()}`
      : '/conversations/stats';

    const response = await this.client.get(url);
    return response.data;
  }

  async getFlowConversations(): Promise<Array<{
    id: string;
    protocolId: string;
    customerName: string | null;
    customerPhone: string;
    status: string;
    startedAt: string;
    sessionData: {
      menuState: string | null;
      selectedOption: string | null;
      lastActivity: string;
    } | null;
  }>> {
    const response = await this.client.get('/conversations/flow/monitor');
    return response.data;
  }

  // Messages
  async sendMessage(conversationId: string, data: SendMessageRequest): Promise<Message> {
    const response = await this.client.post<{ message: Message }>(`/conversations/${conversationId}/messages`, data);
    return response.data.message;
  }

  // Upload file
  async uploadFile(file: File): Promise<{ success: boolean; fileUrl: string; fileName: string; fileSize: number; mimeType: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Agents
  async getAgents(): Promise<any[]> {
    const response = await this.client.get<{ agents: any[] }>('/agents');
    return response.data.agents;
  }

  // Departments
  async getDepartments(): Promise<Department[]> {
    const response = await this.client.get<{ departments: Department[] }>('/departments');
    return response.data.departments;
  }

  async getDepartment(id: string): Promise<Department> {
    const response = await this.client.get<{ department: Department }>(`/departments/${id}`);
    return response.data.department;
  }

  async createDepartment(data: CreateDepartmentRequest): Promise<Department> {
    const response = await this.client.post<{ department: Department }>('/departments', data);
    return response.data.department;
  }

  async updateDepartment(id: string, data: UpdateDepartmentRequest): Promise<Department> {
    const response = await this.client.put<{ department: Department }>(`/departments/${id}`, data);
    return response.data.department;
  }

  async deleteDepartment(id: string): Promise<void> {
    await this.client.delete(`/departments/${id}`);
  }

  // Notifications
  async getNotifications(params?: NotificationQueryParams): Promise<Notification[]> {
    const response = await this.client.get<{ notifications: Notification[] }>('/notifications', { params });
    return response.data.notifications;
  }

  async markNotificationAsRead(id: string): Promise<void> {
    await this.client.put(`/notifications/${id}/read`);
  }

  async markAllNotificationsAsRead(): Promise<void> {
    await this.client.put('/notifications/read-all');
  }

  // Users/Agents
  async getUsers(params?: { departmentId?: string; status?: string; agentStatus?: string }): Promise<User[]> {
    const response = await this.client.get<{ agents: User[] }>('/agents', { params });
    return response.data.agents;
  }

  async createUser(data: CreateUserRequest): Promise<User> {
    const response = await this.client.post<{ agent: User }>('/agents', data);
    return response.data.agent;
  }

  async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
    const response = await this.client.put<{ agent: User }>(`/agents/${id}`, data);
    return response.data.agent;
  }

  async deleteUser(id: string): Promise<void> {
    await this.client.delete(`/agents/${id}`);
  }

  async getLoginHistory(params?: { agentId?: string; startDate?: string; endDate?: string; period?: 'day' | 'week' | 'month'; sessionStatus?: 'all' | 'active' | 'inactive' }): Promise<any[]> {
    const response = await this.client.get<{ history: any[] }>('/agents/login-history', { params });
    return response.data.history;
  }

  async logoutAllAgents(): Promise<{ message: string; count: number }> {
    const response = await this.client.post<{ message: string; count: number }>('/agents/logout-all');
    return response.data;
  }

  // Internal Chat
  async getInternalChatConversations(): Promise<InternalChatConversation[]> {
    const response = await this.client.get<{ conversations: InternalChatConversation[] }>('/internal-chat/conversations');
    return response.data.conversations;
  }

  async getInternalMessages(otherAgentId: string): Promise<InternalMessage[]> {
    const response = await this.client.get<{ messages: InternalMessage[] }>(`/internal-chat/messages/${otherAgentId}`);
    return response.data.messages;
  }

  async sendInternalMessage(
    receiverId: string,
    content: string,
    messageType?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'DOCUMENT',
    mediaUrl?: string,
    originalFilename?: string
  ): Promise<InternalMessage> {
    const response = await this.client.post<{ message: InternalMessage }>('/internal-chat/messages', {
      receiverId,
      content,
      messageType,
      mediaUrl,
      originalFilename,
    });
    return response.data.message;
  }

  async getInternalChatUnreadCount(): Promise<number> {
    const response = await this.client.get<{ count: number }>('/internal-chat/unread-count');
    return response.data.count;
  }

  async getAvailableAgentsForChat(): Promise<User[]> {
    const response = await this.client.get<{ agents: User[] }>('/internal-chat/available-agents');
    return response.data.agents;
  }

  // Agent Status
  async updateAgentStatus(status: string): Promise<{ agent: { id: string; name: string; email: string; status: string } }> {
    const response = await this.client.patch<{ agent: { id: string; name: string; email: string; status: string } }>('/agents/me/status', { status });
    return response.data;
  }

  // Settings
  // Public endpoint - no authentication required - only returns systemName and systemLogo
  async getPublicSettings(): Promise<{
    systemName: string;
    systemLogo: string | null;
  }> {
    const response = await this.client.get<{
      systemName: string;
      systemLogo: string | null;
    }>('/settings/public');
    return response.data;
  }

  // Protected endpoint - requires authentication - returns all settings
  async getSettings(): Promise<{
    systemName: string;
    systemLogo?: string;
    autoLogoutEnabled?: string | boolean;
    autoLogoutHour?: string;
    autoLogoutTimezone?: string;
    autoCloseEnabled?: string | boolean;
    autoCloseHour?: string;
    autoCloseTimezone?: string;
    autoCloseActiveQueueEnabled?: string | boolean;
    autoCloseMenuEnabled?: string | boolean;
    bulkDispatchAutoCloseEnabled?: string | boolean;
    autoCloseMessageEnabled?: string | boolean;
    bulkDispatchBusinessHoursEnabled?: string | boolean;
    bulkDispatchWeekendEnabled?: string | boolean;
    autoCloseMessageHour?: string;
    autoCloseMessageMinute?: string;
    businessHoursStartHour?: string;
    businessHoursStartMinute?: string;
    weekendMessageEnabled?: string | boolean;
    workingDays?: string;
    satisfactionSurveyEnabled?: string | boolean;
    satisfactionSurveyFirstTemplate?: string;
    satisfactionSurveySecondTemplate?: string;
    flowGeneralistEnabled?: string | boolean;
    flowAutomatedEnabled?: string | boolean;
  }> {
    const response = await this.client.get<{
      settings: {
        systemName: { value: string; description?: string | null }
        systemLogo?: { value: string; description?: string | null }
        autoLogoutEnabled?: { value: string; description?: string | null }
        autoLogoutHour?: { value: string; description?: string | null }
        autoLogoutTimezone?: { value: string; description?: string | null }
        autoCloseEnabled?: { value: string; description?: string | null }
        autoCloseHour?: { value: string; description?: string | null }
        autoCloseTimezone?: { value: string; description?: string | null }
        autoCloseActiveQueueEnabled?: { value: string; description?: string | null }
        autoCloseMenuEnabled?: { value: string; description?: string | null }
        bulkDispatchAutoCloseEnabled?: { value: string; description?: string | null }
        autoCloseMessageEnabled?: { value: string; description?: string | null }
        bulkDispatchBusinessHoursEnabled?: { value: string; description?: string | null }
        bulkDispatchWeekendEnabled?: { value: string; description?: string | null }
        autoCloseMessageHour?: { value: string; description?: string | null }
        autoCloseMessageMinute?: { value: string; description?: string | null }
        businessHoursStartHour?: { value: string; description?: string | null }
        businessHoursStartMinute?: { value: string; description?: string | null }
        weekendMessageEnabled?: { value: string; description?: string | null }
        workingDays?: { value: string; description?: string | null }
        satisfactionSurveyEnabled?: { value: string; description?: string | null }
        satisfactionSurveyFirstTemplate?: { value: string; description?: string | null }
        satisfactionSurveySecondTemplate?: { value: string; description?: string | null }
        flowGeneralistEnabled?: { value: string; description?: string | null }
        flowAutomatedEnabled?: { value: string; description?: string | null }
      }
    }>('/settings');
    return {
      systemName: response.data.settings.systemName?.value || 'Itororo SAC',
      systemLogo: response.data.settings.systemLogo?.value,
      autoLogoutEnabled: response.data.settings.autoLogoutEnabled?.value,
      autoLogoutHour: response.data.settings.autoLogoutHour?.value,
      autoLogoutTimezone: response.data.settings.autoLogoutTimezone?.value,
      autoCloseEnabled: response.data.settings.autoCloseEnabled?.value,
      autoCloseHour: response.data.settings.autoCloseHour?.value,
      autoCloseTimezone: response.data.settings.autoCloseTimezone?.value,
      autoCloseActiveQueueEnabled: response.data.settings.autoCloseActiveQueueEnabled?.value,
      autoCloseMenuEnabled: response.data.settings.autoCloseMenuEnabled?.value,
      bulkDispatchAutoCloseEnabled: response.data.settings.bulkDispatchAutoCloseEnabled?.value,
      autoCloseMessageEnabled: response.data.settings.autoCloseMessageEnabled?.value,
      bulkDispatchBusinessHoursEnabled: response.data.settings.bulkDispatchBusinessHoursEnabled?.value,
      bulkDispatchWeekendEnabled: response.data.settings.bulkDispatchWeekendEnabled?.value,
      autoCloseMessageHour: response.data.settings.autoCloseMessageHour?.value,
      autoCloseMessageMinute: response.data.settings.autoCloseMessageMinute?.value,
      businessHoursStartHour: response.data.settings.businessHoursStartHour?.value,
      businessHoursStartMinute: response.data.settings.businessHoursStartMinute?.value,
      weekendMessageEnabled: response.data.settings.weekendMessageEnabled?.value,
      workingDays: response.data.settings.workingDays?.value,
      satisfactionSurveyEnabled: response.data.settings.satisfactionSurveyEnabled?.value,
      satisfactionSurveyFirstTemplate: response.data.settings.satisfactionSurveyFirstTemplate?.value,
      satisfactionSurveySecondTemplate: response.data.settings.satisfactionSurveySecondTemplate?.value,
      flowGeneralistEnabled: response.data.settings.flowGeneralistEnabled?.value,
      flowAutomatedEnabled: response.data.settings.flowAutomatedEnabled?.value,
    };
  }

  async updateSettings(data: {
    systemName: string;
    autoLogoutEnabled?: string;
    autoLogoutHour?: string;
    autoLogoutTimezone?: string;
    autoCloseEnabled?: string;
    autoCloseHour?: string;
    autoCloseTimezone?: string;
    autoCloseActiveQueueEnabled?: string;
    autoCloseMenuEnabled?: string;
    bulkDispatchAutoCloseEnabled?: string;
    autoCloseMessageEnabled?: string;
    bulkDispatchBusinessHoursEnabled?: string;
    bulkDispatchWeekendEnabled?: string;
    autoCloseMessageHour?: string;
    autoCloseMessageMinute?: string;
    businessHoursStartHour?: string;
    businessHoursStartMinute?: string;
    weekendMessageEnabled?: string;
    workingDays?: string;
    satisfactionSurveyEnabled?: string;
    satisfactionSurveyFirstTemplate?: string;
    satisfactionSurveySecondTemplate?: string;
    flowGeneralistEnabled?: string;
    flowAutomatedEnabled?: string;
  }): Promise<void> {
    const settings: any = {
      systemName: {
        value: data.systemName,
        description: 'System/Application name',
      },
    };

    if (data.autoLogoutEnabled !== undefined) {
      settings.autoLogoutEnabled = {
        value: data.autoLogoutEnabled,
        description: 'Enable automatic logout at end of business hours',
      };
    }

    if (data.autoLogoutHour !== undefined) {
      settings.autoLogoutHour = {
        value: data.autoLogoutHour,
        description: 'Hour (0-23) when automatic logout occurs',
      };
    }

    if (data.autoLogoutTimezone !== undefined) {
      settings.autoLogoutTimezone = {
        value: data.autoLogoutTimezone,
        description: 'Timezone for automatic logout',
      };
    }

    if (data.autoCloseEnabled !== undefined) {
      settings.autoCloseEnabled = {
        value: data.autoCloseEnabled,
        description: 'Enable automatic conversation closing at end of business hours',
      };
    }

    if (data.autoCloseHour !== undefined) {
      settings.autoCloseHour = {
        value: data.autoCloseHour,
        description: 'Hour (0-23) when automatic conversation closing occurs',
      };
    }

    if (data.autoCloseTimezone !== undefined) {
      settings.autoCloseTimezone = {
        value: data.autoCloseTimezone,
        description: 'Timezone for automatic conversation closing',
      };
    }

    if (data.autoCloseActiveQueueEnabled !== undefined) {
      settings.autoCloseActiveQueueEnabled = {
        value: data.autoCloseActiveQueueEnabled,
        description: 'Enable automatic closing of ACTIVE, QUEUED, and WAITING conversations',
      };
    }

    if (data.autoCloseMenuEnabled !== undefined) {
      settings.autoCloseMenuEnabled = {
        value: data.autoCloseMenuEnabled,
        description: 'Enable automatic closing of MENU conversations',
      };
    }

    if (data.bulkDispatchAutoCloseEnabled !== undefined) {
      settings.bulkDispatchAutoCloseEnabled = {
        value: data.bulkDispatchAutoCloseEnabled,
        description: 'Enable automatic closing of BULK_DISPATCH conversations',
      };
    }

    if (data.autoCloseMessageEnabled !== undefined) {
      settings.autoCloseMessageEnabled = {
        value: data.autoCloseMessageEnabled,
        description: 'Enable automatic close message when customer contacts outside business hours',
      };
    }

    if (data.bulkDispatchBusinessHoursEnabled !== undefined) {
      settings.bulkDispatchBusinessHoursEnabled = {
        value: data.bulkDispatchBusinessHoursEnabled,
        description: 'Enable business hours close message for BULK_DISPATCH conversations',
      };
    }

    if (data.autoCloseMessageHour !== undefined) {
      settings.autoCloseMessageHour = {
        value: data.autoCloseMessageHour,
        description: 'Hour (0-23) when business hours end',
      };
    }

    if (data.autoCloseMessageMinute !== undefined) {
      settings.autoCloseMessageMinute = {
        value: data.autoCloseMessageMinute,
        description: 'Minute (0-59) when business hours end',
      };
    }

    if (data.businessHoursStartHour !== undefined) {
      settings.businessHoursStartHour = {
        value: data.businessHoursStartHour,
        description: 'Hour (0-23) when business hours start',
      };
    }

    if (data.businessHoursStartMinute !== undefined) {
      settings.businessHoursStartMinute = {
        value: data.businessHoursStartMinute,
        description: 'Minute (0-59) when business hours start',
      };
    }

    if (data.weekendMessageEnabled !== undefined) {
      settings.weekendMessageEnabled = {
        value: data.weekendMessageEnabled,
        description: 'Enable automatic weekend message when customer contacts on non-working days',
      };
    }

    if (data.workingDays !== undefined) {
      settings.workingDays = {
        value: data.workingDays,
        description: 'Working days of the week (0-6, comma-separated, 0=Sunday)',
      };
    }

    if (data.satisfactionSurveyEnabled !== undefined) {
      settings.satisfactionSurveyEnabled = {
        value: data.satisfactionSurveyEnabled,
        description: 'Enable satisfaction survey after closing conversations',
      };
    }

    if (data.satisfactionSurveyFirstTemplate !== undefined) {
      settings.satisfactionSurveyFirstTemplate = {
        value: data.satisfactionSurveyFirstTemplate,
        description: 'Template name for first satisfaction survey question',
      };
    }

    if (data.satisfactionSurveySecondTemplate !== undefined) {
      settings.satisfactionSurveySecondTemplate = {
        value: data.satisfactionSurveySecondTemplate,
        description: 'Template name for second satisfaction survey question',
      };
    }

    if (data.flowGeneralistEnabled !== undefined) {
      settings.flowGeneralistEnabled = {
        value: data.flowGeneralistEnabled,
        description: 'Enable generalist flow (traditional menu with 5 options)',
      };
    }

    if (data.flowAutomatedEnabled !== undefined) {
      settings.flowAutomatedEnabled = {
        value: data.flowAutomatedEnabled,
        description: 'Enable automated flow (new optimized flow)',
      };
    }

    await this.client.put('/settings', { settings });
  }

  async uploadSystemLogo(file: File): Promise<{ logoUrl: string; message: string }> {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await this.client.post<{ logoUrl: string; message: string }>(
      '/settings/logo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async deleteSystemLogo(): Promise<{ message: string }> {
    const response = await this.client.delete<{ message: string }>('/settings/logo');
    return response.data;
  }

  async getSystemInfo(): Promise<{
    environment: string;
    environmentLabel: string;
    whatsappConfigured: boolean;
    whatsappStatus: string;
    whatsappProvider: string | null;
  }> {
    const response = await this.client.get('/settings/system-info');
    return response.data;
  }

  // Ratings
  async getRatings(params?: { rating?: string; startDate?: string; endDate?: string }): Promise<ConversationRating[]> {
    const response = await this.client.get<{ ratings: ConversationRating[] }>('/ratings', { params });
    return response.data.ratings;
  }

  async getRatingStats(params?: { startDate?: string; endDate?: string }): Promise<RatingStats> {
    const response = await this.client.get<{ stats: RatingStats }>('/ratings/stats', { params });
    return response.data.stats;
  }

  async createRating(data: { conversationId: string; rating: string; comment?: string }): Promise<ConversationRating> {
    const response = await this.client.post<{ rating: ConversationRating }>('/ratings', data);
    return response.data.rating;
  }

  // Templates
  async createTemplate(data: any): Promise<{ id: string; status: string; category: string }> {
    const response = await this.client.post<{ id: string; status: string; category: string }>('/templates', data);
    return response.data;
  }

  async getTemplates(): Promise<{ data: any[] }> {
    const response = await this.client.get<{ data: any[] }>('/templates');
    return response.data;
  }

  async getTemplateDetails(name: string): Promise<any> {
    const response = await this.client.get<any>(`/templates/${name}`);
    return response.data;
  }

  async syncTemplateStatuses(): Promise<{ message: string; updated: number }> {
    const response = await this.client.post<{ message: string; updated: number }>('/templates/sync');
    return response.data;
  }

  async deleteTemplate(templateName: string): Promise<{ message: string }> {
    const response = await this.client.delete<{ message: string }>(`/templates/${encodeURIComponent(templateName)}`);
    return response.data;
  }

  async startConversationWithTemplate(data: {
    phoneNumber: string;
    templateName: string;
    components?: any[];
    departmentId?: string;
  }): Promise<{ conversation: Conversation; message: string }> {
    const response = await this.client.post<{ conversation: Conversation; message: string }>(
      '/conversations/start-with-template',
      data
    );
    return response.data;
  }

  // Webhook Status
  async getWebhookStatus(): Promise<{ active: boolean; webhookUrl: string | null }> {
    const response = await this.client.get<{ active: boolean; webhookUrl: string | null }>('/whatsapp/status');
    return response.data;
  }

  // Contacts
  async getContacts(
    params?: ContactQueryParams,
    skipCache = false
  ): Promise<{ data: ContactsResponse; fromCache: boolean }> {
    const headers: Record<string, string> = {};
    if (skipCache) {
      headers['X-Skip-Cache'] = 'true';
    }

    const response = await this.client.get<ContactsResponse>('/contacts', {
      params,
      headers,
    });

    return {
      data: response.data,
      fromCache: response.headers['x-from-cache'] === 'true',
    };
  }

  async createContact(data: CreateContactRequest): Promise<Contact> {
    const response = await this.client.post<{ contact: Contact }>('/contacts', data);
    return response.data.contact;
  }

  async updateContact(id: string, data: UpdateContactRequest): Promise<Contact> {
    const response = await this.client.put<{ contact: Contact }>(`/contacts/${id}`, data);
    return response.data.contact;
  }

  async deleteContact(id: string): Promise<void> {
    await this.client.delete(`/contacts/${id}`);
  }

  async deleteMultipleContacts(ids: string[]): Promise<{ deleted: number }> {
    const response = await this.client.post('/contacts/bulk-delete', { ids });
    return response.data;
  }

  async importContacts(file: File): Promise<ImportContactsResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post<ImportContactsResult>('/contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes timeout for large imports
    });
    return response.data;
  }

  async importContactsFromConversations(): Promise<ImportFromConversationsResult> {
    const response = await this.client.post<ImportFromConversationsResult>('/contacts/import-from-conversations');
    return response.data;
  }

  // Tags
  async getTags(): Promise<Tag[]> {
    const response = await this.client.get<{ tags: Tag[] }>('/tags');
    return response.data.tags;
  }

  async createTag(data: CreateTagRequest): Promise<Tag> {
    const response = await this.client.post<{ tag: Tag }>('/tags', data);
    return response.data.tag;
  }

  async updateTag(id: string, data: UpdateTagRequest): Promise<Tag> {
    const response = await this.client.put<{ tag: Tag }>(`/tags/${id}`, data);
    return response.data.tag;
  }

  async deleteTag(id: string): Promise<void> {
    await this.client.delete(`/tags/${id}`);
  }

  async assignTagsToContact(contactId: string, tagIds: string[]): Promise<Contact> {
    const response = await this.client.post<{ contact: Contact }>(`/tags/contact/${contactId}`, { tagIds });
    return response.data.contact;
  }

  async getContactTags(contactId: string): Promise<Tag[]> {
    const response = await this.client.get<{ tags: Tag[] }>(`/tags/contact/${contactId}`);
    return response.data.tags;
  }

  // Webhook configuration
  async setupWebhook(webhookUrl: string, verifyToken: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post<{ success: boolean; message: string }>('/whatsapp/setup/manual', { webhookUrl, verifyToken });
    return response.data;
  }

  async autoSetupWebhook(baseUrl: string): Promise<{ success: boolean; message: string; configuration?: any }> {
    const response = await this.client.post<{ success: boolean; message: string; configuration?: any }>('/whatsapp/setup', { baseUrl });
    return response.data;
  }

  // Version check
  async getVersion(): Promise<{ version: string; buildTime: string }> {
    const response = await this.client.get<{ version: string; buildTime: string }>('/version');
    return response.data;
  }

  // Satisfaction survey statistics
  async getSatisfactionStats(startDate?: string, endDate?: string): Promise<{
    stats: {
      totalResponses: number;
      totalRatings: number;
      noResponse: number;
      satisfaction: {
        yes: number;
        no: number;
        invalid: number;
        yesPercent: number;
        noPercent: number;
        invalidPercent: number;
      };
      rating: {
        bad: number;
        medium: number;
        good: number;
        invalid: number;
        badPercent: number;
        mediumPercent: number;
        goodPercent: number;
        invalidPercent: number;
      };
    };
    recentResponses: Array<{
      id: string;
      protocolId: string;
      customerName: string;
      customerPhone: string;
      satisfactionResponse: string | null;
      satisfactionResponseValid: boolean;
      rating: string | null;
      ratingValid: boolean;
      createdAt: Date;
    }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await this.client.get(`/satisfaction/stats?${params.toString()}`);
    return response.data;
  }

  // Bulk Dispatches
  async createBulkDispatch(data: any): Promise<any> {
    const response = await this.client.post('/bulk-dispatches', data);
    return response.data;
  }

  async getBulkDispatches(params?: { status?: string; departmentId?: string }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.departmentId) queryParams.append('departmentId', params.departmentId);

    const response = await this.client.get(`/bulk-dispatches?${queryParams.toString()}`);
    return response.data;
  }

  async getBulkDispatch(id: string): Promise<any> {
    const response = await this.client.get(`/bulk-dispatches/${id}`);
    return response.data;
  }

  async cancelBulkDispatch(id: string): Promise<any> {
    const response = await this.client.patch(`/bulk-dispatches/${id}/cancel`);
    return response.data;
  }

  async deleteBulkDispatch(id: string): Promise<any> {
    const response = await this.client.delete(`/bulk-dispatches/${id}`);
    return response.data;
  }

  async getDispatchStats(id: string): Promise<any> {
    const response = await this.client.get(`/bulk-dispatches/${id}/stats`);
    return response.data;
  }

  // Broadcast methods
  async createBroadcast(data: any): Promise<any> {
    const response = await this.client.post('/broadcasts', data);
    return response.data;
  }

  async getBroadcasts(params?: { active?: boolean }): Promise<any> {
    const response = await this.client.get('/broadcasts', { params });
    return response.data;
  }

  async updateBroadcast(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/broadcasts/${id}`, data);
    return response.data;
  }

  async deleteBroadcast(id: string): Promise<any> {
    const response = await this.client.delete(`/broadcasts/${id}`);
    return response.data;
  }

  async toggleBroadcastStatus(id: string, isActive: boolean): Promise<any> {
    const response = await this.client.put(`/broadcasts/${id}/toggle`, { isActive });
    return response.data;
  }

  async resendBroadcast(id: string, sendToAll: boolean): Promise<any> {
    const response = await this.client.post(`/broadcasts/${id}/resend`, { sendToAll });
    return response.data;
  }

  // Flow methods
  async getFlows(params?: { isActive?: boolean; isDefault?: boolean }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params?.isDefault !== undefined) queryParams.append('isDefault', String(params.isDefault));

    const response = await this.client.get(`/flows?${queryParams.toString()}`);
    return response.data;
  }

  async getFlow(id: string): Promise<any> {
    const response = await this.client.get(`/flows/${id}`);
    return response.data;
  }

  async createFlow(data: any): Promise<any> {
    const response = await this.client.post('/flows', data);
    return response.data;
  }

  async updateFlow(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/flows/${id}`, data);
    return response.data;
  }

  async deleteFlow(id: string): Promise<any> {
    const response = await this.client.delete(`/flows/${id}`);
    return response.data;
  }

  async duplicateFlow(id: string, name: string): Promise<any> {
    const response = await this.client.post(`/flows/${id}/duplicate`, { name });
    return response.data;
  }
}

export const api = new ApiService();
